import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { AlertMessage } from './ui/AlertMessage';
import { Modal } from './ui/Modal';
import { formControlClass } from './ui/fieldStyles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function AssignRoles() {
  const [modules, setModules] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingModule, setEditingModule] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [editRows, setEditRows] = useState([]);
  const [moduleVisibleRoles, setModuleVisibleRoles] = useState({});
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');

  const authHeaders = useMemo(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  const sortedPermissions = useMemo(() => {
    const order = { VIEW: 1, VIEW_COMMERCIAL_DATA: 2, CREATE: 3, EDIT: 4 };
    const moduleKey = (editingModule?.moduleKey || editingModule?.key || '').toUpperCase();
    return [...permissions]
      .filter((permission) => {
        const code = (permission.code || '').toUpperCase();
        if (code === 'VIEW_COMMERCIAL_DATA') {
          return moduleKey === 'ORDERS';
        }
        return true;
      })
      .sort((a, b) => {
        const aCode = (a.code || '').toUpperCase();
        const bCode = (b.code || '').toUpperCase();
        return (order[aCode] || 999) - (order[bCode] || 999);
      });
  }, [permissions, editingModule]);

  useEffect(() => {
    fetchModulesAndPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!error) {
      return undefined;
    }
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) {
      return undefined;
    }
    const timer = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(timer);
  }, [success]);

  const fetchModulesAndPermissions = async () => {
    setLoadingModules(true);
    setError('');
    setSuccess('');
    try {
      const [modulesRes, permissionsRes, rolesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/access/modules`, { headers: authHeaders }),
        fetch(`${API_BASE_URL}/api/access/permissions`, { headers: authHeaders }),
        fetch(`${API_BASE_URL}/api/roles/all`, { headers: authHeaders }),
      ]);

      if (!modulesRes.ok || !permissionsRes.ok || !rolesRes.ok) {
        throw new Error('Failed to load modules and permissions');
      }

      const modulesData = await modulesRes.json();
      const permissionsData = await permissionsRes.json();
      const rolesData = await rolesRes.json();

      const modulesList = Array.isArray(modulesData) ? modulesData : [];
      setModules(modulesList);
      setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
      setRoleOptions(Array.isArray(rolesData) ? rolesData : []);

      await loadModuleVisibleRoleSummary(modulesList);
    } catch (err) {
      setError(err.message || 'Unable to load assign roles data');
      setModules([]);
      setPermissions([]);
      setRoleOptions([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const loadModuleVisibleRoleSummary = async (modulesList) => {
    const visibleByModule = {};
    const calls = modulesList
      .filter((module) => module.active)
      .map((module) =>
        fetch(`${API_BASE_URL}/api/access/modules/${module.id}/role-permissions`, {
          headers: authHeaders,
        })
          .then(async (res) => {
            if (!res.ok) {
              return { moduleId: module.id, roles: [] };
            }
            const data = await res.json();
            const roles = Array.isArray(data.roles) ? data.roles : [];
            const visibleRoles = roles
              .filter((row) => (row.permissions || []).includes('VIEW'))
              .map((row) => row.roleName);
            return { moduleId: module.id, roles: visibleRoles };
          })
          .catch(() => ({ moduleId: module.id, roles: [] }))
      );

    const results = await Promise.all(calls);
    results.forEach((item) => {
      visibleByModule[item.moduleId] = item.roles;
    });
    setModuleVisibleRoles(visibleByModule);
  };

  const openEditModal = async (module) => {
    setEditingModule(module);
    setLoadingMatrix(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/access/modules/${module.id}/role-permissions`,
        { headers: authHeaders }
      );
      if (!response.ok) {
        throw new Error('Failed to load role permissions');
      }
      const data = await response.json();
      const serverRows = Array.isArray(data.roles) ? data.roles : [];
      const prefilled = serverRows
        .filter((row) => Array.isArray(row.permissions) && row.permissions.length > 0)
        .map((row, index) => ({
          rowId: Date.now() + index,
          roleId: row.roleId,
          permissions: row.permissions || [],
        }));

      setEditRows(prefilled.length > 0 ? prefilled : [
        { rowId: Date.now(), roleId: '', permissions: [] },
      ]);
    } catch (err) {
      setError(err.message || 'Unable to open module permissions');
      setEditRows([]);
    } finally {
      setLoadingMatrix(false);
    }
  };

  const closeModal = () => {
    setEditingModule(null);
    setEditRows([]);
    setSaving(false);
  };

  const addNewRow = () => {
    if (editRows.length >= roleOptions.length) {
      return;
    }
    setEditRows((prev) => [
      ...prev,
      { rowId: Date.now() + prev.length, roleId: '', permissions: [] },
    ]);
  };

  const removeRow = (rowId) => {
    setEditRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const updateRowRole = (rowId, roleId) => {
    setEditRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? { ...row, roleId: roleId ? String(roleId).trim() : '' }
          : row
      )
    );
  };

  const togglePermission = (rowId, permissionCode) => {
    setEditRows((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) {
          return row;
        }

        const current = new Set(row.permissions || []);
        const code = (permissionCode || '').toUpperCase();

        if (code === 'VIEW') {
          if (current.has('VIEW')) {
            current.delete('VIEW');
            current.delete('CREATE');
            current.delete('EDIT');
            current.delete('VIEW_COMMERCIAL_DATA');
          } else {
            current.add('VIEW');
          }
        } else if (code === 'VIEW_COMMERCIAL_DATA') {
          if (current.has(code)) {
            current.delete(code);
          } else {
            current.add(code);
            current.add('VIEW');
          }
        } else {
          if (current.has(code)) {
            current.delete(code);
          } else {
            current.add(code);
            current.add('VIEW');
          }
        }

        return {
          ...row,
          permissions: Array.from(current),
        };
      })
    );
  };

  const handleSave = async () => {
    if (!editingModule) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const cleanedRows = editRows
        .filter((row) => row.roleId && row.permissions && row.permissions.length > 0)
        .map((row) => ({
          roleId: row.roleId,
          permissions: normalizePermissionSet(row.permissions),
        }));

      const payload = {
        roles: cleanedRows,
      };

      const response = await fetch(
        `${API_BASE_URL}/api/access/modules/${editingModule.id}/role-permissions`,
        {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save module role permissions');
      }

      const roleNameMap = new Map(roleOptions.map((role) => [role.id, role.name]));
      const visibleRoles = cleanedRows
        .filter((row) => normalizePermissionSet(row.permissions).includes('VIEW'))
        .map((row) => roleNameMap.get(row.roleId))
        .filter(Boolean);
      setModuleVisibleRoles((prev) => ({
        ...prev,
        [editingModule.id]: visibleRoles,
      }));

      closeModal();
      setSuccess(`Permissions updated for "${editingModule.name}"`);
    } catch (err) {
      setError(err.message || 'Unable to save role permissions');
    } finally {
      setSaving(false);
    }
  };

  const modulesForGrid = modules.filter((module) => module.active);

  const normalizePermissionSet = (permissionList) => {
    const set = new Set((permissionList || []).map((code) => String(code).toUpperCase()));
    if (set.has('CREATE') || set.has('EDIT')) {
      set.add('VIEW');
    }
    if (set.has('CREATE')) {
      set.add('VIEW_COMMERCIAL_DATA');
    }
    if (!set.has('VIEW')) {
      set.delete('CREATE');
      set.delete('EDIT');
      set.delete('VIEW_COMMERCIAL_DATA');
    }
    return Array.from(set);
  };

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar tone="light" title="Assign Roles to Modules" />
        </CardHeader>
        <CardBody>
          <AlertMessage
            message={success}
            type="success"
            onClose={() => setSuccess('')}
            className="mb-4"
          />
          <AlertMessage
            message={error}
            type="error"
            onClose={() => setError('')}
            className="mb-4"
          />

          {loadingModules ? (
            <div className="py-8 text-center text-gray-500">Loading modules...</div>
          ) : modulesForGrid.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No modules found</div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Module</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Visible to Roles</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modulesForGrid.map((module) => (
                    <tr key={module.id} className="border-b last:border-b-0 border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800">{module.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {(moduleVisibleRoles[module.id] || []).length === 0 ? (
                          <span className="text-gray-400">No role assigned</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {(moduleVisibleRoles[module.id] || []).map((roleName) => (
                              <span
                                key={`${module.id}-${roleName}`}
                                className="px-2 py-1 rounded-md text-xs bg-gray-900 text-white"
                              >
                                {roleName}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => openEditModal(module)}
                          className="p-2 rounded-lg text-primary-700 hover:bg-primary-50 transition-colors"
                          title="Edit permissions"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={Boolean(editingModule)}
        onClose={closeModal}
        title="Edit module permissions"
        description={editingModule ? `Module: ${editingModule.name}` : ''}
        size="xl"
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        )}
        footer={(
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={addNewRow}
              disabled={saving || loadingMatrix || editRows.length >= roleOptions.length}
            >
              + Add role row
            </Button>
            <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSave} loading={saving} disabled={saving || loadingMatrix}>
              Update
            </Button>
          </>
        )}
      >
        {editingModule && (
          <div className="py-5 sm:py-6">
              {loadingMatrix ? (
                <div className="py-8 text-center text-slate-500">Loading role permissions...</div>
              ) : roleOptions.length === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  No roles found. Create roles first in Roles tab.
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200/80 shadow-sm">
                  <table className="w-full">
                    <thead className="bg-slate-50/90 border-b border-slate-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Role</th>
                        {sortedPermissions.map((permission) => (
                          <th key={permission.code} className="text-center py-3 px-4 text-sm font-semibold text-slate-700">
                            {permission.name}
                          </th>
                        ))}
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editRows.map((row) => (
                        <tr key={row.rowId} className="border-b last:border-b-0 border-slate-100">
                          <td className="py-3 px-4">
                            {(() => {
                              const selectedRoleIds = new Set(
                                editRows
                                  .filter((r) => r.rowId !== row.rowId && r.roleId)
                                  .map((r) => String(r.roleId).trim())
                              );
                              const availableRoles = roleOptions.filter(
                                (role) =>
                                  String(role.id) === String(row.roleId) ||
                                  !selectedRoleIds.has(String(role.id))
                              );

                              return (
                            <select
                              value={row.roleId}
                              onChange={(e) => updateRowRole(row.rowId, e.target.value)}
                              className={formControlClass({ className: 'py-2.5 text-sm' })}
                            >
                              <option value="">Select role</option>
                              {availableRoles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                              );
                            })()}
                          </td>
                          {sortedPermissions.map((permission) => {
                            const checked = (row.permissions || []).includes(permission.code.toUpperCase());
                            return (
                              <td key={`${row.rowId}-${permission.code}`} className="py-3 px-4 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePermission(row.rowId, permission.code.toUpperCase())}
                                  className="w-4 h-4 rounded-md text-primary-600 border-slate-300 focus:ring-primary-500/30"
                                />
                              </td>
                            );
                          })}
                          <td className="py-3 px-4 text-center">
                            <button
                              type="button"
                              onClick={() => removeRow(row.rowId)}
                              className="p-2 rounded-xl text-rose-600 hover:bg-rose-50 transition-colors"
                              title="Remove row"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            <p className="mt-3 text-xs text-slate-500">
              Note: <span className="font-medium">Create</span> and <span className="font-medium">Edit</span> automatically include <span className="font-medium">View</span>.
              Removing <span className="font-medium">View</span> will also remove <span className="font-medium">Create</span> and <span className="font-medium">Edit</span>.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AssignRoles;
